import { useState, useRef } from 'react'
import { Upload, Image } from 'lucide-react'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useBusinessStore } from '../../core/stores/businessStore'
import { Business } from '../../core/models/business'
import Modal from '../../components/ui/Modal'
import SaveButton from '../../components/buttons/SaveButton'
import CancelButton from '../../components/buttons/CancelButton'
import toast from 'react-hot-toast'

interface UploadLogoModalProps {
  business: Business
  isOpen: boolean
  onClose: () => void
}

export default function UploadLogoModal({ business, isOpen, onClose }: UploadLogoModalProps) {
  const { t } = useTranslation()
  const { uploadLogo } = useBusinessStore()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(t('invalidImageFile') || 'Please select a valid image file')
        return
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error(t('fileTooLarge') || 'File size is too large. Maximum size is 2MB')
        return
      }

      setSelectedFile(file)
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    try {
      const success = await uploadLogo(business.id, selectedFile)
      if (success) {
        toast.success(t('logoUploaded'))
        setSelectedFile(null)
        setPreview(null)
        onClose()
      }
    } catch (error: any) {
      console.error('Failed to upload logo:', error)
      // Error message is already displayed by axios interceptor for HTTP errors
      // Only show fallback if no error response was received (network error, etc.)
      if (!error?.response) {
        toast.error(error.message || t('error') || 'Failed to upload logo')
      }
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setPreview(null)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('uploadLogo')}
      showCancelButton={false}
    >
      <div className="space-y-6">
        <div className="text-center">
          <h4 className="font-medium text-gray-900 mb-2">{business.name}</h4>
          <p className="text-sm text-gray-600">
            {t('selectLogoImage') || 'Select an image for the logo (PNG, JPG, JPEG - Maximum 2MB)'}
          </p>
        </div>

        {/* Current Logo */}
        {business.logoUrl && !preview && (
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">{t('currentLogo') || 'Current Logo:'}</p>
            <img
              src={business.logoUrl}
              alt="Current logo"
              className="w-24 h-24 mx-auto rounded-lg object-cover border-2 border-gray-200"
            />
          </div>
        )}

        {/* File Upload Area */}
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          {preview ? (
            <div>
              <img
                src={preview}
                alt="Preview"
                className="w-24 h-24 mx-auto rounded-lg object-cover mb-2 border-2 border-gray-200"
              />
              <p className="text-sm text-gray-600">{t('logoPreview') || 'New Logo Preview'}</p>
            </div>
          ) : (
            <div>
              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">
                {t('clickToSelectImage') || 'Click to select image or drag file here'}
              </p>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {selectedFile && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Image className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700 flex-1 truncate">{selectedFile.name}</span>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <SaveButton
            onClick={handleUpload}
            isLoading={isUploading}
            className='px-8 py-2 text-xs'
            disabled={!selectedFile || isUploading}
            label={t('uploadLogo') || 'Upload Logo'}
          />
          <CancelButton
            onClick={handleClose}
            disabled={isUploading}
            className='px-8 py-2 text-xs'
          />
        </div>
      </div>
    </Modal>
  )
}
